import { useEffect, useState } from 'react';

/**
 * Viewport queries, keyed to the same scale Tailwind uses.
 *
 * The previous version of this file carried the Bootstrap scale
 * (576/768/992/1200/1540) left over from the Falcon theme. Nothing consumed
 * it, which is just as well: a component that switched layout at 576 while the
 * CSS around it switched at 640 would have had a 64px band where the two
 * disagreed. Keep these in step with `screens` in tailwind.config.js.
 */
export const SCREENS = { sm: 640, md: 768, lg: 1024, xl: 1280, '2xl': 1536 };

/** Subscribes to a media query. Returns false during SSR / before mount. */
const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(
    () => typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia(query).matches
      : false
  );

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mql = window.matchMedia(query);
    const onChange = (e) => setMatches(e.matches);
    setMatches(mql.matches);
    // addListener is the Safari < 14 spelling; both are kept so the hook does
    // not throw on the older iPads still in the field.
    if (mql.addEventListener) mql.addEventListener('change', onChange);
    else mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', onChange);
      else mql.removeListener(onChange);
    };
  }, [query]);

  return matches;
};

/** True below Tailwind's `sm` — i.e. phone width. */
export const useIsMobile = () => useMediaQuery(`(max-width: ${SCREENS.sm - 1}px)`);

/**
 * True for a touch-first device, by input capability rather than width.
 * A tablet at 1024px still deserves 44px tap targets; a narrow desktop window
 * does not.
 */
export const useIsTouch = () => useMediaQuery('(pointer: coarse)');

export const useBreakpoints = () => {
  const [size, setSize] = useState(() => ({
    width: typeof window === 'undefined' ? 0 : window.innerWidth,
    height: typeof window === 'undefined' ? 0 : window.innerHeight,
  }));

  useEffect(() => {
    const onResize = () => setSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return {
    width: size.width,
    height: size.height,
    breakpoints: {
      up: (bp) => size.width >= SCREENS[bp],
      down: (bp) => size.width < SCREENS[bp],
    },
  };
};

export default useBreakpoints;
